// ============================================
// Save/Load UI — Export, Import, Save Slot Display
// Extracted from main.js
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { fetchStoryJson } from '../engine/story-fetch.js';
import { variableManager } from '../engine/variables.js';

import { saveSystem } from '../systems/save.js';
import { inventoryManager } from '../systems/inventory.js';
import { appearanceState } from '../systems/appearance.js';
import { loadGameIncremental } from '../systems/game-loader.js';
import { L } from '../systems/localization.js';
import { cloudSaveManager } from '../systems/cloud-save.js';

import { layoutManager } from './layout.js';
import { choicesUI } from './choices.js';
import { phoneUI } from './phone.js';
import { shopUI } from './shop.js';
import { statsUI } from './stats.js';

/**
 * @param {object} opts - { setCharacterData, loaderOpts }
 *   setCharacterData(data) - sets App.characterData
 *   loaderOpts - opts forwarded to loadGameIncremental
 */
let _opts = {};

// Cached cloud-save list ([{slot, size, updated_at}]) so the badges render
// synchronously during refreshSlots without re-hitting the network on every
// open. Refreshed when the save/load overlay opens (refreshCloudList).
let _cloudList = [];
let _cloudListFetchedAt = 0;

export const saveLoadUI = {
    /**
     * Provide App-level callbacks that can't be imported.
     */
    init(opts = {}) {
        _opts = opts;
    },

    /**
     * Whether cloud sync is live (kill-switch on + available + enabled + has a
     * code). Cheap synchronous probe used to gate badge rendering.
     */
    _cloudActive() {
        try {
            return cloudSaveManager.isAvailable()
                && cloudSaveManager.isEnabled()
                && cloudSaveManager.hasCode();
        } catch (e) { return false; }
    },

    /** Slots the current build is allowed to sync (free=autosave+1, patreon=7). */
    _cloudAllowedSlots() {
        try { return cloudSaveManager.allowedSlots() || []; } catch (e) { return []; }
    },

    /**
     * Refresh the cached cloud-save list, then re-render the slot badges.
     * Called when the save/load (pause) overlay opens. Non-blocking — badges
     * render from the previous cache immediately and update once the pull
     * resolves. Throttled to ~one network call per 5 s.
     */
    async refreshCloudList(force = false) {
        if (!this._cloudActive()) {
            _cloudList = [];
            try { this._renderCloudBadges(); } catch (e) {}
            return;
        }
        const now = Date.now();
        if (!force && (now - _cloudListFetchedAt) < 5000) {
            try { this._renderCloudBadges(); } catch (e) {}
            return;
        }
        _cloudListFetchedAt = now;
        try {
            // pullList returns {ok, saves:[...]} — unwrap (same wrapper class
            // as the pullSlot C1 review finding; a bare array is tolerated
            // defensively in case the manager API ever changes).
            const list = await cloudSaveManager.pullList();
            const arr = Array.isArray(list) ? list
                : (list && list.ok && Array.isArray(list.saves)) ? list.saves
                : null;
            if (arr) _cloudList = arr;
        } catch (e) {
            // Keep the previous cache on error — never blank the badges on a
            // transient network hiccup. (catch(e) per Safari module rule.)
            console.warn('[CloudSave] pullList failed:', e?.message);
        }
        try { this._renderCloudBadges(); } catch (e) {}
    },

    /** Find the cloud entry for a given slot from the cache. */
    _cloudEntry(slot) {
        return _cloudList.find(e => e && Number(e.slot) === Number(slot)) || null;
    },

    /**
     * Render the per-slot cloud badge + Patreon lock hint.
     * Reads the cached list; mutates only textContent / hidden / title (no
     * innerHTML replacement, so no listener orphaning).
     */
    _renderCloudBadges() {
        const active = this._cloudActive();
        const allowed = active ? this._cloudAllowedSlots() : [];
        document.querySelectorAll('.save-slot-row').forEach(row => {
            const slot = parseInt(row.dataset.slot);
            const badge = row.querySelector('.slot-cloud-badge');

            // Cloud off entirely → hide both, done.
            if (!active) {
                if (badge) { badge.hidden = true; badge.className = 'slot-cloud-badge'; badge.removeAttribute('title'); }
                return;
            }

            const eligible = allowed.length === 0 ? true : allowed.map(Number).includes(Number(slot));
            const entry = this._cloudEntry(slot);

            if (badge) {
                if (!eligible) {
                    // Non-eligible slot → small Patreon lock hint instead of a sync badge.
                    badge.hidden = false;
                    badge.className = 'slot-cloud-badge slot-cloud-locked';
                    badge.textContent = L.get('save.cloud_patreon', 'Local only');
                    badge.title = L.get('save.cloud_patreon_hint', 'Cloud sync for this slot is a Patreon perk.');
                } else if (entry) {
                    badge.hidden = false;
                    badge.className = 'slot-cloud-badge slot-cloud-synced';
                    badge.textContent = L.get('save.cloud_badge', '☁ Cloud saved');
                    badge.title = this._cloudTooltip(entry);
                } else {
                    badge.hidden = true;
                    badge.className = 'slot-cloud-badge';
                    badge.removeAttribute('title');
                }
            }

        });
    },

    /** Tooltip text for a cloud entry (day if known, else date). The cloud
     *  list carries only {slot, size, updated_at} (day lives inside the
     *  ciphertext), so we surface the server timestamp. */
    _cloudTooltip(entry) {
        let when = entry.updated_at || '';
        try {
            const d = new Date(entry.updated_at);
            if (!Number.isNaN(d.getTime())) when = d.toLocaleString();
        } catch (e) {}
        return (L.get('save.cloud_updated', 'Cloud copy from') + ' ' + when).trim();
    },

    /**
     * Export a save slot as a downloadable JSON file.
     * @param {number} slot - -1 = auto-save, 0..5 = manual slots. Defaults to auto for backcompat.
     */
    async exportSave(slot = -1) {
        try {
            const isAuto = slot === -1;
            const storageKey = isAuto ? 'PnO_AutoSave' : `PnO_Save_${slot}`;

            // For the auto-save export, force a fresh auto-save first so the
            // file always reflects current state. Manual slots are user-
            // controlled — exporting them must NOT trigger a new write
            // (otherwise "Export Slot 1" would overwrite Slot 1's snapshot
            // with the live state, defeating the purpose of multiple slots).
            if (isAuto && saveSystem._collectors) {
                await saveSystem.autoSave();
            }

            // Dual-read — same logic as saveSystem.loadAutoSave(). On Firefox
            // + itch.io iframe, IndexedDB writes can silently stop persisting
            // after a while (foundby:Amanda S 2026-04-21 "stuck at day 6"),
            // while the localStorage fallback keeps working. Always pick the
            // newer of the two by timestamp so export matches what the game
            // actually loads at startup.
            //
            // Both reads also wrapped — IDB read can THROW in private mode,
            // not just return null.
            let saveData = null;
            try {
                saveData = await localforage.getItem(storageKey);
            } catch(_) {}
            try {
                const raw = localStorage.getItem(storageKey);
                if (raw) {
                    const lsData = JSON.parse(raw);
                    if (!saveData) {
                        saveData = lsData;
                    } else if (lsData.timestamp && lsData.timestamp > (saveData.timestamp || '')) {
                        saveData = lsData;
                    }
                }
            } catch(_) {}

            // Last-resort fallback for the auto-save export only: storage
            // didn't write all session (private browsing with both backends
            // blocked), but the run is still in RAM. Build a fresh snapshot
            // from live collectors so the player can still escape with a
            // backup. Manual slots can't fall back this way — the slot's
            // saved state may differ from the live state.
            if (!saveData && isAuto && saveSystem._collectors) {
                console.warn('[PnO] Export: storage empty, building from live game state');
                events.emit(Events.TOAST_REQUEST,
                    'Storage unavailable — exporting current game state.',
                    'toast-warn');
                saveData = saveSystem.buildSaveData(saveSystem._collectors);
                saveData.slotIndex = -1;
            }

            if (!saveData) {
                console.warn(`[PnO] No save data available to export (slot=${slot})`);
                events.emit(Events.TOAST_REQUEST,
                    isAuto ? 'No save data to export.' : `Slot ${slot + 1} is empty.`,
                    'toast-error');
                return;
            }
            const json = JSON.stringify(saveData, null, 2);
            const day = saveData.day ?? saveData.baseStats?.Day ?? '?';
            const filename = isAuto
                ? `pinked_save_auto_day${day}.json`
                : `pinked_save_slot${slot + 1}_day${day}.json`;

            // Diagnostic emit — every Export Save click ships a small telemetry
            // event so the "exports always Day 1" class bug can be diagnosed
            // from telemetry alone, without needing player repro steps.
            // Foundby:VPS player 2026-05-08 (single-report, couldn't repro).
            try {
                let lsTs = null, idbTs = null, drift = null;
                try {
                    const lsRaw = localStorage.getItem(storageKey);
                    if (lsRaw) lsTs = JSON.parse(lsRaw)?.timestamp || null;
                } catch(_) {}
                try {
                    const idbData = await localforage.getItem(storageKey);
                    idbTs = idbData?.timestamp || null;
                } catch(_) {}
                if (lsTs && idbTs) drift = lsTs === idbTs ? 'equal' : (lsTs > idbTs ? 'ls_newer' : 'idb_newer');
                const liveDay = (() => {
                    try { return window._sm?.currentStory?.variablesState?.['Day']; } catch(_) { return null; }
                })();
                events.emit(Events.SAVE_EXPORT_DIAGNOSTIC, {
                    day,
                    liveDay,
                    hasInkState: !!saveData.inkState,
                    inkStateLen: saveData.inkState?.length || 0,
                    lsTs, idbTs, drift,
                    isAuto, slot,
                    saveVersion: saveData.saveVersion,
                    gameVersion: saveData.gameVersion,
                });
            } catch(_) { /* never block export on telemetry */ }

            // Android (Capacitor WebView) doesn't reliably trigger an
            // <a download> click on a blob URL — there's no native
            // DownloadListener wired up, so the click is a no-op and
            // nothing reaches disk (foundby:VPS report 2026-04-26 v0.2.4.2,
            // Android 11 MI 9). Route via navigator.share() instead, which
            // hands the JSON to the OS share sheet (Drive, Files, email,
            // etc.). Falls back to a copy-to-clipboard overlay when share
            // isn't available, so the player can always escape with their
            // save text manually.
            if (window.Capacitor) {
                try {
                    const file = new File([json], filename, { type: 'application/json' });
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            title: 'Pinked & Owned save',
                        });
                        events.emit(Events.TOAST_REQUEST, 'Save shared.', 'toast-success');
                        return;
                    }
                } catch (shareErr) {
                    // AbortError = user cancelled the share sheet — bail quietly.
                    if (shareErr && shareErr.name === 'AbortError') return;
                    console.warn('[PnO] navigator.share failed, falling back to clipboard:', shareErr);
                }
                this._showExportClipboard(json, filename);
                return;
            }

            // Standard browser path. Prefer the File System Access API when
            // available (Chrome/Edge/WebView2 desktop): the player gets a real
            // "Save as" dialog. A bare <a download> click either saves silently
            // to the Downloads folder or gets dropped entirely in sandboxed
            // iframes / bare WebViews — while the success toast still fires,
            // which reads as "export is broken" (foundby report 0.3 Free:
            // "message pop-up appears, system save menu doesn't").
            if (typeof window.showSaveFilePicker === 'function') {
                try {
                    const handle = await window.showSaveFilePicker({
                        suggestedName: filename,
                        types: [{
                            description: 'Pinked & Owned save',
                            accept: { 'application/json': ['.json'] },
                        }],
                    });
                    const writable = await handle.createWritable();
                    await writable.write(json);
                    await writable.close();
                    events.emit(Events.TOAST_REQUEST, 'Save exported.', 'toast-success');
                    return;
                } catch (fsErr) {
                    // AbortError = player cancelled the dialog — done, no fallback.
                    if (fsErr && fsErr.name === 'AbortError') return;
                    // SecurityError etc. (cross-origin iframe like itch.io) —
                    // fall through to the classic anchor download.
                    console.warn('[PnO] showSaveFilePicker failed, falling back to <a download>:', fsErr?.message);
                }
            }

            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            events.emit(Events.TOAST_REQUEST, 'Save exported. Check your Downloads folder.', 'toast-success');
        } catch (e) {
            console.error('[PnO] Export failed:', e);
            events.emit(Events.TOAST_REQUEST, 'Export failed: ' + (e?.message || 'unknown error'), 'toast-error');
        }
    },

    /**
     * Last-resort export UI for Android WebView when navigator.share is
     * unavailable: pops a modal with the JSON in a textarea and a Copy
     * button. Player pastes into Files / Drive / email manually. Same
     * shape as a tiny overlay-style modal — no dependency on a separate
     * overlay module.
     */
    _showExportClipboard(json, filename) {
        // Re-use a single overlay instance across calls.
        let overlay = document.getElementById('overlay-export-clipboard');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'overlay-export-clipboard';
            overlay.className = 'overlay overlay-export-clipboard';
            overlay.innerHTML = `
                <div class="overlay-box export-clipboard-box">
                    <h2 data-l10n="ui.export_save">Export Save</h2>
                    <p class="export-clipboard-hint" data-l10n="ui.export_clipboard_hint">Copy the text below and paste it into a notes app or file. Save it as <code>pinked_save.json</code>.</p>
                    <textarea class="export-clipboard-text" readonly></textarea>
                    <div class="export-clipboard-actions">
                        <button class="btn-primary export-clipboard-copy" data-l10n="ui.copy">Copy</button>
                        <button class="btn-secondary export-clipboard-close" data-l10n="ui.close">Close</button>
                    </div>
                </div>
            `;
            document.body.appendChild(overlay);
        }
        const ta = overlay.querySelector('.export-clipboard-text');
        const copyBtn = overlay.querySelector('.export-clipboard-copy');
        const closeBtn = overlay.querySelector('.export-clipboard-close');
        ta.value = json;
        overlay.classList.remove('hidden');

        const onCopy = async () => {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(json);
                } else {
                    ta.select();
                    document.execCommand('copy');
                }
                events.emit(Events.TOAST_REQUEST, 'Save copied to clipboard.', 'toast-success');
            } catch (err) {
                events.emit(Events.TOAST_REQUEST, 'Copy failed — select the text manually.', 'toast-error');
            }
        };
        const onClose = () => {
            overlay.classList.add('hidden');
            copyBtn.removeEventListener('click', onCopy);
            closeBtn.removeEventListener('click', onClose);
        };
        // Remove handlers bound by a previous open (overlay is cached + reused)
        // so re-opening without clicking Close doesn't accumulate listeners.
        if (copyBtn._onCopy) copyBtn.removeEventListener('click', copyBtn._onCopy);
        if (closeBtn._onClose) closeBtn.removeEventListener('click', closeBtn._onClose);
        copyBtn._onCopy = onCopy;
        closeBtn._onClose = onClose;
        copyBtn.addEventListener('click', onCopy);
        closeBtn.addEventListener('click', onClose);
    },

    importSave() {
        const input = document.getElementById('import-file-input');
        if (!input) return;

        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
                const text = await file.text();
                let data = JSON.parse(text);

                // Unwrap Unity export wrapper (e.g. {"PnO_GameSave_Slot1": {...}})
                const wrapperKey = Object.keys(data).find(k => k.startsWith('PnO_'));
                if (wrapperKey && Object.keys(data).length === 1) {
                    let inner = data[wrapperKey];
                    if (typeof inner === 'string') inner = JSON.parse(inner);
                    data = inner;
                }

                // Convert Unity format to web format if needed
                if (Array.isArray(data.baseKeys) && Array.isArray(data.baseValues)) {
                    const { convertUnitySave } = await import('../systems/save-converter.js');
                    data = convertUnitySave(data);
                    console.log('[PnO] Converted Unity save to web format');
                }

                // Save to localforage (IndexedDB)
                await localforage.setItem('PnO_AutoSave', data);
                console.log('[PnO] Save imported to localforage');

                // Load directly into running game (avoids Safari iframe storage loss on reload)
                try {
                    const storyJson = await fetchStoryJson();

                    storyManager.initStory(storyJson);
                    choicesUI.setChoiceHandler((index) => storyManager.selectChoice(index));
                    appearanceState.registerInkReader((v) => {
                        try { return storyManager.currentStory?.variablesState[v]; } catch(e) { return undefined; }
                    });
                    shopUI.setStoryReader(() => storyManager.currentStory);
                    if (data.characterData && _opts.setCharacterData) {
                        _opts.setCharacterData(data.characterData);
                    }
                    inventoryManager.giveStarterItems();
                    inventoryManager.registerInkPusher((name, value) => {
                        if (typeof value === 'boolean') variableManager.setFlag(name, value);
                        else variableManager.setBuffedVar(name, value);
                    });
                    inventoryManager.registerInkReader((name) => {
                        const story = storyManager.currentStory;
                        if (!story) return 0;
                        try { return story.variablesState[name]; } catch(e) { return 0; }
                    });

                    loadGameIncremental(data, _opts.loaderOpts || {});
                    phoneUI.setStoryRef(storyManager.currentStory);
                    layoutManager.closeOverlay('overlay-pause');
                    events.emit(Events.SCREEN_CHANGE, 'screen-game-view');
                    console.log('[PnO] Import: loaded directly into game');
                } catch (loadErr) {
                    console.warn('[PnO] Direct load failed, falling back to reload:', loadErr);
                    window.location.reload();
                }
            } catch (err) {
                console.error('[PnO] Import failed:', err);
                if (typeof statsUI !== 'undefined') {
                    statsUI.showToast?.('Import failed: ' + err.message, 'notif-error');
                }
            }
        };
        input.click();
    },

    refreshSlots() {
        document.querySelectorAll('.save-slot-row').forEach(row => {
            const slot = parseInt(row.dataset.slot);
            const infoEl = row.querySelector('.slot-info');
            const loadBtn = row.querySelector('.slot-btn-load');
            const exportBtn = row.querySelector('.slot-btn-export');
            const deleteBtn = row.querySelector('.slot-btn-delete');
            if (!infoEl) return;

            const meta = slot === -1 ? saveSystem.getAutoSaveMeta() : saveSystem.getSlotMeta(slot);
            if (meta) {
                const day = meta.day ?? '?';
                const name = meta.playerName ?? 'Player';
                const time = meta.timestamp ?? '';
                // Audit D15: render slot info with a path-indicator pill +
                // stat preview so slots are distinguishable. innerHTML is
                // safe here — name comes from sanitized PlayerName, path
                // from a fixed enum, day/fem/sub are integers.
                const path = meta.path || 'Independent';
                const pathSlug = path.toLowerCase();
                const fem = Math.round(meta.fem || 0);
                const sub = Math.round(meta.sub || 0);
                const safeName = String(name).replace(/[<>&"']/g, c => ({
                    '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'
                })[c]);
                infoEl.innerHTML =
                    `<span class="slot-name">${safeName}</span>` +
                    ` <span class="slot-day">Day ${day}</span>` +
                    ` <span class="slot-path-pill slot-path-${pathSlug}">${path}</span>` +
                    ` <span class="slot-stat-pill slot-stat-fem">F ${fem}</span>` +
                    ` <span class="slot-stat-pill slot-stat-sub">S ${sub}</span>` +
                    `<div class="slot-timestamp">${time}</div>`;
                infoEl.removeAttribute('data-l10n');
                row.classList.add('save-slot-has-data');
                row.dataset.path = pathSlug;
                if (loadBtn) loadBtn.disabled = false;
                if (exportBtn) exportBtn.disabled = false;
                if (deleteBtn) deleteBtn.disabled = false;
            } else {
                infoEl.textContent = L.get('save.empty');
                infoEl.setAttribute('data-l10n', 'save.empty');
                row.classList.remove('save-slot-has-data');
                delete row.dataset.path;
                if (loadBtn) loadBtn.disabled = true;
                // Auto-save export still allowed when empty — falls back to
                // live game state via buildSaveData (escape hatch for private-
                // browsing players whose IDB+LS writes both silently failed).
                if (exportBtn) exportBtn.disabled = slot !== -1;
                if (deleteBtn) deleteBtn.disabled = true;
            }
        });
        // Re-render cloud badges from the cached list (cheap, no network).
        try { this._renderCloudBadges(); } catch (e) {}
    },

    /**
     * Audit D01: confirm overwrite dialog. Returns a Promise<boolean> — true
     * to proceed with save, false to cancel. Reads existing slot meta to
     * show what the player is about to overwrite (name, day, path).
     */
    confirmOverwrite(slot) {
        return new Promise(resolve => {
            const meta = saveSystem.getSlotMeta(slot);
            if (!meta) { resolve(true); return; }  // empty slot — no confirm needed

            const overlay = document.createElement('div');
            overlay.className = 'save-confirm-overlay';
            overlay.style.cssText = 'position:fixed;inset:0;background:rgba(8,6,12,0.85);z-index:700;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);';

            const path = meta.path || 'Independent';
            const day = meta.day ?? '?';
            const name = meta.playerName ?? 'Player';
            const time = meta.timestamp ?? '';
            const safeName = String(name).replace(/[<>&"']/g, c => ({
                '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'
            })[c]);

            const dialog = document.createElement('div');
            dialog.className = 'save-confirm-dialog';
            dialog.style.cssText = 'max-width:420px;width:90%;background:var(--bg-panel);border:1px solid var(--border-glow);border-radius:12px;padding:24px;color:var(--text);box-shadow:0 20px 60px rgba(0,0,0,0.6);';
            dialog.innerHTML = `
                <h3 style="margin:0 0 8px 0;color:var(--rose);font-size:18px;">Overwrite Slot ${slot + 1}?</h3>
                <p style="margin:0 0 16px 0;color:var(--text-body);font-size:14px;line-height:1.5;">
                    Slot ${slot + 1} currently holds <strong>${safeName}</strong> on the ${path} route,
                    Day ${day} <span style="color:var(--text-dim);">(${time})</span>.
                    Saving will replace this run. This cannot be undone.
                </p>
                <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:20px;">
                    <button class="save-confirm-cancel" style="min-height:44px;padding:10px 18px;background:transparent;border:1px solid var(--border);border-radius:8px;color:var(--text-body);font-size:14px;cursor:pointer;">Cancel</button>
                    <button class="save-confirm-ok" style="min-height:44px;padding:10px 18px;background:rgba(196,80,80,0.25);border:1px solid rgba(220,120,120,0.65);border-radius:8px;color:#ffd0d0;font-size:14px;font-weight:600;cursor:pointer;">Overwrite</button>
                </div>
            `;
            overlay.appendChild(dialog);
            document.body.appendChild(overlay);

            const cleanup = (result) => {
                overlay.remove();
                document.removeEventListener('keydown', onKey);
                resolve(result);
            };
            const onKey = (e) => {
                if (e.key === 'Escape') cleanup(false);
                else if (e.key === 'Enter') cleanup(true);
            };
            dialog.querySelector('.save-confirm-cancel').addEventListener('click', () => cleanup(false));
            dialog.querySelector('.save-confirm-ok').addEventListener('click', () => cleanup(true));
            overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(false); });
            document.addEventListener('keydown', onKey);
            // Focus the cancel button so accidental Enter doesn't confirm overwrite.
            setTimeout(() => dialog.querySelector('.save-confirm-cancel')?.focus(), 50);
        });
    },

    /**
     * Delete-slot confirmation (community request, AWolfe 2026-05-31). Mirrors
     * confirmOverwrite — returns Promise<boolean> (true = delete, false =
     * cancel). Destructive, so it's stricter than the overwrite dialog: Enter
     * does NOT auto-confirm (only an explicit Delete click), Escape and
     * click-outside cancel, and focus starts on Cancel.
     */
    confirmDelete(slot) {
        return new Promise(resolve => {
            const meta = saveSystem.getSlotMeta(slot);
            if (!meta) { resolve(false); return; }  // empty slot — nothing to delete

            const overlay = document.createElement('div');
            overlay.className = 'save-confirm-overlay';
            overlay.style.cssText = 'position:fixed;inset:0;background:rgba(8,6,12,0.85);z-index:700;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);';

            const path = meta.path || 'Independent';
            const day = meta.day ?? '?';
            const name = meta.playerName ?? 'Player';
            const time = meta.timestamp ?? '';
            const safeName = String(name).replace(/[<>&"']/g, c => ({
                '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'
            })[c]);

            const title = (L.get('save.delete_title', 'Delete Slot {0}?') || 'Delete Slot {0}?').replace('{0}', slot + 1);
            let warn = L.get('save.delete_warn', 'This permanently deletes this save. It cannot be undone.');
            // Cloud sync active + this slot has a cloud copy → it is deleted
            // as well (otherwise the boot sync would resurrect the save).
            try {
                if (this._cloudActive() && this._cloudAllowedSlots().includes(slot) && this._cloudEntry(slot)) {
                    warn += ' ' + L.get('save.delete_cloud_note', 'The cloud copy is deleted as well.');
                }
            } catch (e) {}
            const delLbl = L.get('save.action_delete', 'Delete');

            const dialog = document.createElement('div');
            dialog.className = 'save-confirm-dialog';
            dialog.style.cssText = 'max-width:420px;width:90%;background:var(--bg-panel);border:1px solid var(--border-glow);border-radius:12px;padding:24px;color:var(--text);box-shadow:0 20px 60px rgba(0,0,0,0.6);';
            dialog.innerHTML = `
                <h3 style="margin:0 0 8px 0;color:#ff9aa8;font-size:18px;">${title}</h3>
                <p style="margin:0 0 16px 0;color:var(--text-body);font-size:14px;line-height:1.5;">
                    <strong>${safeName}</strong> <span style="color:var(--text-dim);">&middot; ${path} &middot; Day ${day} (${time})</span><br>
                    ${warn}
                </p>
                <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:20px;">
                    <button class="save-confirm-cancel" style="min-height:44px;padding:10px 18px;background:transparent;border:1px solid var(--border);border-radius:8px;color:var(--text-body);font-size:14px;cursor:pointer;">Cancel</button>
                    <button class="save-confirm-ok" style="min-height:44px;padding:10px 18px;background:rgba(196,80,80,0.25);border:1px solid rgba(220,120,120,0.65);border-radius:8px;color:#ffd0d0;font-size:14px;font-weight:600;cursor:pointer;">${delLbl}</button>
                </div>
            `;
            overlay.appendChild(dialog);
            document.body.appendChild(overlay);

            const cleanup = (result) => {
                overlay.remove();
                document.removeEventListener('keydown', onKey);
                resolve(result);
            };
            // Destructive action: Escape cancels, Enter does NOT auto-confirm.
            const onKey = (e) => { if (e.key === 'Escape') cleanup(false); };
            dialog.querySelector('.save-confirm-cancel').addEventListener('click', () => cleanup(false));
            dialog.querySelector('.save-confirm-ok').addEventListener('click', () => cleanup(true));
            overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(false); });
            document.addEventListener('keydown', onKey);
            // Focus Cancel so an accidental Enter/Space can't delete a save.
            setTimeout(() => dialog.querySelector('.save-confirm-cancel')?.focus(), 50);
        });
    },
};
